import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { AllStatusProject } from '@app/interfaces/analytics';
import {
  MetricObjType,
  MetricListArrType,
} from '@app/interfaces/pages-types/anatylics-metric.type';
import {
  MetricTemplatesGroupType,
  MetricTemplatesType,
} from '@app/interfaces/slices-types/metric-templates.type';

import { toast } from 'react-toastify';
import { deepClone } from '@app/utils/deep-clone';

import useTranslation from '@app/hooks/use-translation';

import { useGetChatGptGroupsWithMetricsQuery } from '@app/store/api/chat-api/gpt-metric-chat.api';
import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';
import { useGetGptGroupsWithMetricsQuery } from '@app/store/api/gpt-metric.api';

import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';
import { useOutletContext } from 'react-router-dom';

import { CompositeMetricVisualization } from '@app/interfaces/metric.type';

import MetricTemplatesModal from '../../analytics-metric-tab/blocks/modals/metric-templates-modal';

import CompositeMetricBlock from './composite-metric.block';
type CompositeMetricWrapperPropsType = {
  show: boolean;
  onClose: () => void;
  label: ReactNode;
  metricSettings: MetricObjType;
  editModal: boolean;
  copyModal: boolean;
  metricList?: MetricListArrType[];
  status?: AllStatusProject | undefined;
  currentTemplate: 'chat' | 'voice';
  metricTemplates: MetricTemplatesGroupType[] | undefined;
  createFromTemplate?: boolean;
  metricTemplateSettings?: MetricTemplatesType | undefined;
  onCloseCalculated?(): void;
};

const CompositeMetricWrapper: FC<CompositeMetricWrapperPropsType> = (props) => {
  const { metricSettings, currentTemplate, onCloseCalculated, ...rest } = props;
  // add from template helpers metric_name_err

  const { project } = useOutletContext<AnalyticPageOutlet | undefined>() || {};
  const { t } = useTranslation('pages.metric');

  // gpt
  const { data: userInfo } = useGetUserInfoQuery();
  const project_id = project?.project_id || '';
  const { data: gptGroupsListVoice } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id } },
    {
      skip: !project_id || !userInfo?.features.gpt || currentTemplate === 'chat',
    },
  );
  const { data: gptGroupsListChat } = useGetChatGptGroupsWithMetricsQuery(
    { params: { project_id } },
    {
      skip: !project_id || !userInfo?.features.gpt || currentTemplate === 'voice',
    },
  );

  const gptGroupsList = useMemo(
    () => ({ chat: gptGroupsListChat, voice: gptGroupsListVoice }),
    [gptGroupsListChat, gptGroupsListVoice],
  );

  const gpt_metrics = useMemo(
    () => gptGroupsList?.[currentTemplate]?.gpt_groups.flatMap((group) => group.gpt_metrics) || [],
    [currentTemplate, gptGroupsList],
  );
  // gpt

  const findMetricWithSameName = useCallback(
    (name: string, metricName: string) => {
      const result = [...(gpt_metrics || []), ...(rest.metricList || [])]?.find(
        (metric) => metric.name === name,
      )?.metric_id;
      if (!result) {
        toast.error(t('metric_name_err', { name: metricName }));
        return 'null';
      }
      return result;
    },
    [gpt_metrics, rest.metricList, t],
  );

  const checkFormulaFromTemplate = useCallback(
    (obj) => {
      if (typeof obj !== 'object' || obj === null) return;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (key === 'metric_name') {
            const oldValue = obj[key];
            const newValue = findMetricWithSameName(oldValue, oldValue);
            delete obj[key];
            obj['metric_id'] = newValue;
          } else {
            checkFormulaFromTemplate(obj[key]);
          }
        }
      }
    },
    [findMetricWithSameName],
  );

  const templateFormulaResult = useCallback(
    (obj) => {
      const newObj = deepClone(obj);
      checkFormulaFromTemplate(newObj);
      return newObj;
    },
    [checkFormulaFromTemplate],
  );

  // add from template helpers

  const [metricSettingsFormatted, changeMetricSettingsFormatted] = useState(
    metricSettings?.type === 'calculated' ? metricSettings : undefined,
  );

  // fill from template feature
  function fillMetricTrigger(metric: MetricTemplatesType) {
    const formula = templateFormulaResult(metric.settings.formula);

    changeMetricSettingsFormatted({
      ...metric,
      visualization: metric.visualization as CompositeMetricVisualization,
      settings: {
        ...metric.settings,
        formula,
      },
    });
  }
  const [openTemplates, setOpenTemplatesState] = useState(false);
  function onCloseTemplates() {
    setOpenTemplatesState(false);
  }
  function handleOpenTemplates() {
    setOpenTemplatesState(true);
  }
  // fill from template feature

  return (
    <>
      <CompositeMetricBlock
        gpt_metrics={gpt_metrics}
        metricSettings={metricSettingsFormatted}
        currentTemplate={currentTemplate}
        handleOpenTemplates={handleOpenTemplates}
        onCloseCalculated={onCloseCalculated}
        {...rest}
      />
      {/* fill from template modal  */}
      <MetricTemplatesModal
        onClose={onCloseTemplates}
        showModal={openTemplates}
        type={currentTemplate || 'voice'}
        filterTypeTarget={'calculated'}
        fillMetricTrigger={fillMetricTrigger}
      />
      {/* fill from template modal  */}
    </>
  );
};

export default CompositeMetricWrapper;
